import React, { useState, useEffect, useCallback } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { aiAutomationApi } from '../../utils/aiAutomationApi';

const TIERS = [
  { key: 'HEAVY', label: 'Heavy', desc: 'Complex generation — JD creation, SQL queries, long emails', defaultTemp: 0.3, defaultTokens: 1000 },
  { key: 'MID', label: 'Mid', desc: 'Routing, classification, medium responses', defaultTemp: 0.2, defaultTokens: 500 },
  { key: 'LIGHT', label: 'Light', desc: 'Extraction, short responses, humanization', defaultTemp: 0.1, defaultTokens: 300 },
  { key: 'EMBEDDING', label: 'Embedding', desc: 'Vector search and text similarity', defaultTemp: 0.0, defaultTokens: 0 },
  { key: 'VOICE_STT', label: 'Voice to Text', desc: 'Candidate speech transcription for AI interviews and voice verification', defaultTemp: 0.0, defaultTokens: 0 },
];

const TEXT_TIERS = TIERS.filter(t => t.key !== 'VOICE_STT');
const VOICE_TIER = TIERS.find(t => t.key === 'VOICE_STT');

const PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'deepseek', label: 'DeepSeek' },
  { value: 'qwen', label: 'Qwen / Alibaba' },
  { value: 'moonshot', label: 'Moonshot / Kimi' },
  { value: 'groq', label: 'Groq' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'together', label: 'Together AI' },
];

const STT_PROVIDERS = [
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'elevenlabs', label: 'ElevenLabs' },
  { value: 'local_faster_whisper', label: 'Local Faster Whisper' },
];

const CUSTOM_MODEL_VALUE = '__custom_model__';
const DEFAULT_TTS_ENGINES = ['piper', 'elevenlabs'];
const DEFAULT_ELEVENLABS_MODELS = ['eleven_turbo_v2_5', 'eleven_flash_v2_5', 'eleven_multilingual_v2'];
const DEFAULT_ELEVENLABS_VOICES = [
  { name: 'Rachel', gender: 'Female', voice_id: '21m00Tcm4TlvDq8ikWAM' },
  { name: 'Bella', gender: 'Female', voice_id: 'EXAVITQu4vr4xnSDxMaL' },
  { name: 'Elli', gender: 'Female', voice_id: 'MF3mGyEYCl7XYWbV9V6O' },
  { name: 'Antoni', gender: 'Male', voice_id: 'ErXwobaYiN019PkySvjV' },
  { name: 'Josh', gender: 'Male', voice_id: 'TxGEqnHWrfWFTfGW9XjX' },
  { name: 'Adam', gender: 'Male', voice_id: 'pNInz6obpgDQGcFmaJgB' },
];

const StatusIcon = ({ success }) => (
  <svg
    className="mt-0.5 h-4 w-4 shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    {success ? (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    ) : (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    )}
  </svg>
);

const MODELS_BY_PROVIDER = {
  openai: {
    chat: [
      { value: 'gpt-5', label: 'GPT-5 (best accuracy)', tier: ['HEAVY'] },
      { value: 'gpt-5-mini', label: 'GPT-5 Mini (balanced accuracy/cost)', tier: ['HEAVY', 'MID'] },
      { value: 'gpt-5-nano', label: 'GPT-5 Nano (lowest cost)', tier: ['LIGHT'] },
      { value: 'gpt-4.1', label: 'GPT-4.1 (high accuracy fallback)', tier: ['HEAVY'] },
      { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini (balanced)', tier: ['MID', 'LIGHT'] },
      { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano (fast extraction)', tier: ['LIGHT'] },
      { value: 'gpt-4o', label: 'GPT-4o (legacy high accuracy)', tier: ['HEAVY', 'MID'] },
      { value: 'gpt-4o-mini', label: 'GPT-4o Mini (legacy low cost)', tier: ['MID', 'LIGHT'] },
    ],
    embedding: [
      { value: 'text-embedding-3-small', label: 'Text Embedding 3 Small (recommended low cost)', tier: ['EMBEDDING'] },
      { value: 'text-embedding-3-large', label: 'Text Embedding 3 Large (higher accuracy)', tier: ['EMBEDDING'] },
    ],
    stt: [
      { value: 'whisper-1', label: 'Whisper 1', tier: ['VOICE_STT'] },
      { value: 'gpt-4o-mini-transcribe', label: 'GPT-4o Mini Transcribe', tier: ['VOICE_STT'] },
      { value: 'gpt-4o-transcribe', label: 'GPT-4o Transcribe', tier: ['VOICE_STT'] },
    ],
  },
  openrouter: {
    chat: [
      { value: 'deepseek/deepseek-r1', label: 'DeepSeek R1 (reasoning, high accuracy)', tier: ['HEAVY'] },
      { value: 'deepseek/deepseek-chat', label: 'DeepSeek Chat/V3 (balanced)', tier: ['HEAVY', 'MID', 'LIGHT'] },
      { value: 'qwen/qwen3-235b-a22b', label: 'Qwen3 235B A22B (high accuracy)', tier: ['HEAVY', 'MID'] },
      { value: 'qwen/qwen3-32b', label: 'Qwen3 32B (balanced/low cost)', tier: ['MID', 'LIGHT'] },
      { value: 'qwen/qwq-32b', label: 'QwQ 32B (reasoning, low cost)', tier: ['HEAVY', 'MID'] },
      { value: 'moonshotai/kimi-k2', label: 'Kimi K2 (agentic reasoning)', tier: ['HEAVY', 'MID'] },
      { value: 'openai/gpt-5', label: 'OpenAI GPT-5 (best accuracy)', tier: ['HEAVY'] },
      { value: 'openai/gpt-5-mini', label: 'OpenAI GPT-5 Mini (balanced)', tier: ['HEAVY', 'MID'] },
      { value: 'openai/gpt-5-nano', label: 'OpenAI GPT-5 Nano (lowest cost)', tier: ['LIGHT'] },
      { value: 'openai/gpt-4.1', label: 'OpenAI GPT-4.1', tier: ['HEAVY'] },
      { value: 'openai/gpt-4.1-mini', label: 'OpenAI GPT-4.1 Mini', tier: ['MID', 'LIGHT'] },
      { value: 'anthropic/claude-opus-4.1', label: 'Claude Opus 4.1 (deep reasoning)', tier: ['HEAVY'] },
      { value: 'anthropic/claude-sonnet-4.5', label: 'Claude Sonnet 4.5 (accurate/balanced)', tier: ['HEAVY', 'MID'] },
      { value: 'anthropic/claude-haiku-4.5', label: 'Claude Haiku 4.5 (fast/low cost)', tier: ['MID', 'LIGHT'] },
      { value: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro (high accuracy)', tier: ['HEAVY'] },
      { value: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash (balanced)', tier: ['MID', 'LIGHT'] },
      { value: 'meta-llama/llama-3.3-70b-instruct', label: 'Llama 3.3 70B (open model)', tier: ['HEAVY', 'MID'] },
      { value: 'mistralai/mistral-large-latest', label: 'Mistral Large (balanced)', tier: ['HEAVY', 'MID'] },
    ],
    embedding: [],
    stt: [
      { value: 'microsoft/mai-transcribe-2', label: 'Microsoft MAI-Transcribe 2', tier: ['VOICE_STT'] },
      { value: 'openai/whisper-large-v3', label: 'OpenAI Whisper Large v3', tier: ['VOICE_STT'] },
      { value: 'openai/whisper-1', label: 'OpenAI Whisper 1', tier: ['VOICE_STT'] },
    ],
  },
  elevenlabs: {
    chat: [],
    embedding: [],
    stt: [
      { value: 'scribe_v2', label: 'Scribe v2', tier: ['VOICE_STT'] },
      { value: 'scribe_v1', label: 'Scribe v1', tier: ['VOICE_STT'] },
    ],
  },
  local_faster_whisper: {
    chat: [],
    embedding: [],
    stt: [
      { value: 'tiny', label: 'Tiny', tier: ['VOICE_STT'] },
      { value: 'base', label: 'Base', tier: ['VOICE_STT'] },
      { value: 'small', label: 'Small', tier: ['VOICE_STT'] },
      { value: 'medium', label: 'Medium', tier: ['VOICE_STT'] },
    ],
  },
  deepseek: {
    chat: [
      { value: 'deepseek-reasoner', label: 'DeepSeek Reasoner / R1 (best reasoning)', tier: ['HEAVY'] },
      { value: 'deepseek-chat', label: 'DeepSeek Chat / V3 (accurate + low cost)', tier: ['HEAVY', 'MID', 'LIGHT'] },
    ],
    embedding: [],
  },
  qwen: {
    chat: [
      { value: 'qwen-max', label: 'Qwen Max (best accuracy)', tier: ['HEAVY'] },
      { value: 'qwen-plus', label: 'Qwen Plus (balanced accuracy/cost)', tier: ['HEAVY', 'MID'] },
      { value: 'qwen-turbo', label: 'Qwen Turbo (fast/low cost)', tier: ['MID', 'LIGHT'] },
      { value: 'qwen-long', label: 'Qwen Long (long context)', tier: ['HEAVY', 'MID'] },
    ],
    embedding: [],
  },
  moonshot: {
    chat: [
      { value: 'kimi-k2-0711-preview', label: 'Kimi K2 Preview (best reasoning)', tier: ['HEAVY'] },
      { value: 'moonshot-v1-128k', label: 'Moonshot 128K (long context)', tier: ['HEAVY', 'MID'] },
      { value: 'moonshot-v1-32k', label: 'Moonshot 32K (balanced)', tier: ['MID', 'LIGHT'] },
      { value: 'moonshot-v1-8k', label: 'Moonshot 8K (low cost)', tier: ['LIGHT'] },
    ],
    embedding: [],
  },
  groq: {
    chat: [
      { value: 'openai/gpt-oss-120b', label: 'GPT-OSS 120B (best Groq accuracy)', tier: ['HEAVY', 'MID'] },
      { value: 'openai/gpt-oss-20b', label: 'GPT-OSS 20B (fast/low cost)', tier: ['MID', 'LIGHT'] },
      { value: 'llama-3.3-70b-versatile', label: 'Llama 3.3 70B Versatile', tier: ['HEAVY', 'MID'] },
      { value: 'llama-3.1-8b-instant', label: 'Llama 3.1 8B Instant', tier: ['LIGHT'] },
      { value: 'gemma2-9b-it', label: 'Gemma 2 9B (cheap extraction)', tier: ['LIGHT'] },
    ],
    embedding: [],
  },
  anthropic: {
    chat: [
      { value: 'claude-opus-4-1-20250805', label: 'Claude Opus 4.1 (best reasoning)', tier: ['HEAVY'] },
      { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5 (accurate/balanced)', tier: ['HEAVY', 'MID'] },
      { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4 (stable fallback)', tier: ['HEAVY', 'MID'] },
      { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (fast/low cost)', tier: ['MID', 'LIGHT'] },
    ],
    embedding: [],
  },
  together: {
    chat: [
      { value: 'meta-llama/Llama-3.3-70B-Instruct-Turbo', label: 'Llama 3.3 70B Turbo (balanced)', tier: ['HEAVY', 'MID'] },
      { value: 'meta-llama/Llama-3.1-70B-Instruct-Turbo', label: 'Llama 3.1 70B Turbo (fallback)', tier: ['HEAVY', 'MID'] },
      { value: 'meta-llama/Llama-3.1-8B-Instruct-Turbo', label: 'Llama 3.1 8B Turbo (low cost)', tier: ['MID', 'LIGHT'] },
      { value: 'mistralai/Mixtral-8x7B-Instruct-v0.1', label: 'Mixtral 8x7B (low cost)', tier: ['LIGHT'] },
    ],
    embedding: [],
  },
};

function getModelsForTier(provider, tier) {
  const providerModels = MODELS_BY_PROVIDER[provider] || MODELS_BY_PROVIDER.openai;
  if (tier === 'VOICE_STT') {
    const models = providerModels.stt || [];
    return models.length > 0 ? models : [];
  }
  const isEmbedding = tier === 'EMBEDDING';
  const models = isEmbedding ? providerModels.embedding : providerModels.chat;
  if (!models || models.length === 0) return [];
  const tierFiltered = models.filter(m => m.tier.includes(tier));
  return tierFiltered.length > 0 ? tierFiltered : models;
}

// Reusable provider config form (used for both main and fallback)
function ProviderConfigForm({ tierKey, role, data, onChange, onSave, onTest, onRemove, saving, testing, testResult, primaryColor, isEmbedding, isVoiceStt, showApiKey, toggleApiKey, defaultTemp, defaultTokens }) {
  const provider = data?.provider || (isVoiceStt ? 'openrouter' : 'openai');
  const providers = isVoiceStt ? STT_PROVIDERS : PROVIDERS;
  const availableModels = getModelsForTier(provider, tierKey);
  const noModelsAvailable = availableModels.length === 0;
  const isTest = testResult?.tier === tierKey && testResult?.role === role;
  const isFallback = role === 'fallback';
  const savingKey = `${tierKey}:${role}`;
  const isLocalVoiceStt = isVoiceStt && provider === 'local_faster_whisper';
  const rawSelectedModel = data?.model || (availableModels[0]?.value || '');
  const isKnownModel = availableModels.some(m => m.value === rawSelectedModel);
  const selectedModel = isLocalVoiceStt && !isKnownModel ? (availableModels[0]?.value || '') : rawSelectedModel;
  const isCustomModel = !isLocalVoiceStt && Boolean(data?.use_custom_model || (selectedModel && !isKnownModel));

  return (
    <div className={`${isFallback ? 'pl-4 border-l-2 border-blue-200 dark:border-blue-800' : ''} rounded-xl bg-slate-50/70 p-3 dark:bg-gray-950/40 sm:p-4`}>
      {/* Provider + Model */}
      <div className="grid gap-3 mb-3 lg:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Provider</label>
          <select
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900"
            value={provider}
            onChange={e => onChange(tierKey, role, 'provider', e.target.value)}
          >
            {providers.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Model</label>
          {noModelsAvailable ? (
            <div className="w-full px-3 py-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-700 dark:text-amber-400">
              {isEmbedding
                ? `${PROVIDERS.find(p => p.value === provider)?.label} doesn't support embeddings. Use OpenAI.`
                : isVoiceStt
                  ? `${STT_PROVIDERS.find(p => p.value === provider)?.label || provider} does not have a preset STT model here. Use custom model ID.`
                  : 'No models available for this provider.'}
            </div>
          ) : (
            <div className="space-y-2">
              <select
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900"
                value={isCustomModel ? CUSTOM_MODEL_VALUE : selectedModel}
                onChange={e => {
                  if (e.target.value === CUSTOM_MODEL_VALUE) {
                    onChange(tierKey, role, 'use_custom_model', true);
                    return;
                  }
                  onChange(tierKey, role, 'model_from_select', e.target.value);
                }}
              >
                {availableModels.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
                {!isLocalVoiceStt && <option value={CUSTOM_MODEL_VALUE}>Custom model ID...</option>}
              </select>
              {isCustomModel && (
                <input
                  type="text"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900"
                  value={selectedModel}
                  onChange={e => onChange(tierKey, role, 'model', e.target.value)}
                  placeholder="Enter exact provider model ID"
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* API Key (+ Temp/Tokens for main only) */}
      <div className={`grid ${isFallback || isEmbedding || isVoiceStt ? 'grid-cols-1' : 'lg:grid-cols-3'} gap-3 mb-3`}>
        {isLocalVoiceStt ? (
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
            Runs inside the backend with the installed <span className="font-mono">faster_whisper</span> package. Model weights load separately on first use or from cache. If the package or model is unavailable, candidate voice uses the browser transcript fallback.
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">API Key</label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                className="w-full px-3 py-2.5 pr-16 truncate rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900"
                value={data?.api_key || ''}
                onChange={e => onChange(tierKey, role, 'api_key', e.target.value)}
                placeholder={data?.has_api_key ? 'Saved (enter to change)' : 'Enter API key'}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                onClick={toggleApiKey}
              >
                {showApiKey ? 'Hide' : 'Show'}
              </button>
            </div>
            {data?.has_api_key && !data?.api_key && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">Key saved in database</p>
            )}
          </div>
        )}
        {!isFallback && !isEmbedding && !isVoiceStt && (
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Temperature</label>
            <input
              type="number" step="0.1" min="0" max="2"
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900"
              value={data?.temperature ?? defaultTemp}
              onChange={e => onChange(tierKey, role, 'temperature', e.target.value)}
            />
          </div>
        )}
        {!isFallback && !isEmbedding && !isVoiceStt && (
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Max Tokens</label>
            <input
              type="number" min="0"
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900"
              value={data?.max_tokens ?? defaultTokens}
              onChange={e => onChange(tierKey, role, 'max_tokens', e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Test result */}
      {isTest && (
        <div className={`flex items-start gap-2 text-sm p-3 rounded-lg mb-3 ${testResult.success ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 border border-green-200 dark:border-green-800' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 border border-red-200 dark:border-red-800'}`}>
          <StatusIcon success={testResult.success} />
          <span>{testResult.message}</span>
        </div>
      )}

      {/* Buttons */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          onClick={() => onTest(tierKey, role)}
          disabled={testing === savingKey || noModelsAvailable}
          className="px-4 py-2 text-sm rounded-lg border border-gray-300 bg-white dark:border-gray-700 text-gray-700 dark:bg-gray-900 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {testing === savingKey ? 'Testing...' : isVoiceStt ? 'Validate Setup' : 'Test Connection'}
        </button>
        <button
          onClick={() => onSave(tierKey, role)}
          disabled={saving === savingKey || noModelsAvailable}
          className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50 transition-colors"
          style={{ backgroundColor: primaryColor }}
        >
          {saving === savingKey ? 'Saving...' : 'Save'}
        </button>
        {isFallback && onRemove && (
          <button
            onClick={() => onRemove(tierKey)}
            className="px-4 py-2 text-sm rounded-lg border border-red-300 bg-white dark:border-red-700 text-red-600 dark:bg-gray-900 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors sm:ml-auto"
          >
            Remove Fallback
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminAIConfigPage() {
  const { settings } = useSettings();
  const primaryColor = settings?.theme?.colors?.primary || '#3B5BDB';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // "HEAVY:main" or "HEAVY:fallback"
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [mainState, setMainState] = useState({});
  const [fallbackState, setFallbackState] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [showApiKey, setShowApiKey] = useState({});
  const [expandedFallback, setExpandedFallback] = useState({});
  const [mediaConfig, setMediaConfig] = useState(null);
  const [mediaSaving, setMediaSaving] = useState(false);
  const [mediaTesting, setMediaTesting] = useState(false);
  const [mediaTestResult, setMediaTestResult] = useState(null);
  const [showElevenLabsKey, setShowElevenLabsKey] = useState(false);

  const fetchConfigs = useCallback(async ({ silent = false, preserveScroll = false } = {}) => {
    const scrollY = preserveScroll && typeof window !== 'undefined' ? window.scrollY : null;
    if (!silent) setLoading(true);
    try {
      const res = await aiAutomationApi.get('/admin/ai-config');
      if (res?.success) {
        const main = {};
        const fallback = {};
        const nextExpandedFallback = {};
        res.configs.forEach(c => {
          if (c.role === 'fallback') {
            fallback[c.tier] = { ...c };
            if (c.configured) {
              nextExpandedFallback[c.tier] = true;
            }
          } else {
            main[c.tier] = { ...c };
          }
        });
        setMainState(main);
        setFallbackState(fallback);
        if (res.media) {
          setMediaConfig(res.media);
        }
        if (Object.keys(nextExpandedFallback).length) {
          setExpandedFallback(prev => ({ ...prev, ...nextExpandedFallback }));
        }
      }
    } catch (err) {
      console.error('Failed to load AI configs', err);
    } finally {
      if (!silent) setLoading(false);
      if (scrollY != null && typeof window !== 'undefined') {
        window.requestAnimationFrame(() => window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' }));
      }
    }
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const handleFieldChange = (tier, role, field, value) => {
    setTestResult(null);
    const setter = role === 'fallback' ? setFallbackState : setMainState;
    setter(prev => {
      const updated = { ...prev, [tier]: { ...prev[tier], [field]: value } };
      if (field === 'provider') {
        const models = getModelsForTier(value, tier);
        updated[tier].model = models.length > 0 ? models[0].value : '';
        updated[tier].use_custom_model = false;
      }
      if (field === 'model_from_select') {
        updated[tier].model = value;
        updated[tier].use_custom_model = false;
      }
      if (field === 'use_custom_model') {
        updated[tier].use_custom_model = Boolean(value);
      }
      return updated;
    });
  };

  const handleSave = async (tier, role = 'main') => {
    const savingKey = `${tier}:${role}`;
    setSaving(savingKey);
    setSaveSuccess(null);
    try {
      const data = role === 'fallback' ? fallbackState[tier] : mainState[tier];
      const provider = data?.provider || (tier === 'VOICE_STT' ? 'openrouter' : 'openai');
      const isLocalVoiceStt = tier === 'VOICE_STT' && provider === 'local_faster_whisper';
      const availableModels = getModelsForTier(provider, tier);
      const modelIsAllowed = availableModels.some(m => m.value === data?.model);
      const model = isLocalVoiceStt && !modelIsAllowed
        ? (availableModels[0]?.value || '')
        : (data?.model || (availableModels[0]?.value || ''));
      if (!model.trim()) {
        setTestResult({ tier, role, success: false, message: 'Please select or enter a model before saving.' });
        setSaving(null);
        return;
      }
      const res = await aiAutomationApi.put('/admin/ai-config', {
        tier: data?.tier || tier,
        provider,
        model,
        api_key: isLocalVoiceStt ? '' : (data?.api_key || undefined),
        base_url: data?.base_url || undefined,
        temperature: data?.temperature != null ? parseFloat(data.temperature) : undefined,
        max_tokens: data?.max_tokens != null ? parseInt(data.max_tokens) : undefined,
        role,
      });
      if (res?.success) {
        setSaveSuccess(savingKey);
        setTimeout(() => setSaveSuccess(null), 3000);
      }
      await fetchConfigs({ silent: true, preserveScroll: true });
    } catch (err) {
      console.error('Failed to save config', err);
      setTestResult({ tier, role, success: false, message: `Save failed: ${err.message}` });
    }
    setSaving(null);
  };

  const handleTest = async (tier, role = 'main') => {
    const savingKey = `${tier}:${role}`;
    setTesting(savingKey);
    setTestResult(null);
    try {
      const data = role === 'fallback' ? fallbackState[tier] : mainState[tier];
      const provider = data?.provider || (tier === 'VOICE_STT' ? 'openrouter' : 'openai');
      const isLocalVoiceStt = tier === 'VOICE_STT' && provider === 'local_faster_whisper';
      const availableModels = getModelsForTier(provider, tier);
      const modelIsAllowed = availableModels.some(m => m.value === data?.model);
      const model = isLocalVoiceStt && !modelIsAllowed
        ? (availableModels[0]?.value || '')
        : (data?.model || (availableModels[0]?.value || ''));
      if (!model.trim()) {
        setTestResult({ tier, role, success: false, message: 'Please select or enter a model before testing.' });
        setTesting(null);
        return;
      }
      const res = await aiAutomationApi.post('/admin/ai-config/test', {
        provider,
        model,
        api_key: isLocalVoiceStt ? undefined : (data?.api_key || undefined),
        base_url: data?.base_url || undefined,
        capability: tier === 'VOICE_STT' ? 'stt' : undefined,
      });
      setTestResult({ tier, role, success: res?.success, message: res?.message || 'Unknown result' });
    } catch (err) {
      setTestResult({ tier, role, success: false, message: `Test failed: ${err.message}` });
    }
    setTesting(null);
  };

  const handleRemoveFallback = async (tier) => {
    try {
      await aiAutomationApi.delete(`/admin/ai-config/fallback?tier=${tier}`);
      setExpandedFallback(prev => ({ ...prev, [tier]: false }));
      await fetchConfigs({ silent: true, preserveScroll: true });
    } catch (err) {
      console.error('Failed to remove fallback', err);
    }
  };

  const mediaTts = mediaConfig?.tts || {};
  const ttsEngines = mediaConfig?.tts_engines || DEFAULT_TTS_ENGINES;
  const elevenLabsModels = mediaConfig?.elevenlabs_models || DEFAULT_ELEVENLABS_MODELS;
  const elevenLabsVoices = mediaConfig?.elevenlabs_voices || DEFAULT_ELEVENLABS_VOICES;
  const selectedTtsEngine = mediaTts.engine || 'piper';
  const isElevenLabsTts = selectedTtsEngine === 'elevenlabs';

  const handleMediaTtsChange = (field, value) => {
    setMediaTestResult(null);
    setMediaConfig(prev => {
      const current = prev || {
        tts: {},
        tts_engines: DEFAULT_TTS_ENGINES,
        elevenlabs_models: DEFAULT_ELEVENLABS_MODELS,
        elevenlabs_voices: DEFAULT_ELEVENLABS_VOICES,
      };
      return {
        ...current,
        tts: {
          ...(current.tts || {}),
          [field]: value,
        },
      };
    });
  };

  const saveMediaTts = async () => {
    setMediaSaving(true);
    setMediaTestResult(null);
    try {
      const res = await aiAutomationApi.put('/admin/ai-config/media-tts', {
        engine: selectedTtsEngine,
        elevenlabs_api_key: mediaTts.elevenlabs_api_key || undefined,
        elevenlabs_voice_id: mediaTts.elevenlabs_voice_id || elevenLabsVoices[0]?.voice_id,
        elevenlabs_model: mediaTts.elevenlabs_model || elevenLabsModels[0],
      });
      if (res?.media) setMediaConfig(res.media);
      setMediaTestResult({ success: true, message: res?.message || 'AI audio settings saved.' });
    } catch (err) {
      setMediaTestResult({ success: false, message: `Save failed: ${err.message}` });
    } finally {
      setMediaSaving(false);
    }
  };

  const testMediaTts = async () => {
    setMediaTesting(true);
    setMediaTestResult(null);
    try {
      const res = await aiAutomationApi.post('/admin/ai-config/media-tts/test', {
        engine: selectedTtsEngine,
        elevenlabs_api_key: mediaTts.elevenlabs_api_key || undefined,
        elevenlabs_voice_id: mediaTts.elevenlabs_voice_id || elevenLabsVoices[0]?.voice_id,
        elevenlabs_model: mediaTts.elevenlabs_model || elevenLabsModels[0],
      });
      setMediaTestResult({ success: res?.success, message: res?.message || 'Unknown result' });
    } catch (err) {
      setMediaTestResult({ success: false, message: `Test failed: ${err.message}` });
    } finally {
      setMediaTesting(false);
    }
  };

  const resetMediaTts = async () => {
    setMediaSaving(true);
    setMediaTestResult(null);
    try {
      const res = await aiAutomationApi.delete('/admin/ai-config/media-tts');
      if (res?.media) setMediaConfig(res.media);
      setMediaTestResult({ success: true, message: res?.message || 'AI audio settings reset to defaults.' });
    } catch (err) {
      setMediaTestResult({ success: false, message: `Reset failed: ${err.message}` });
    } finally {
      setMediaSaving(false);
    }
  };

  return (
    <div className="min-h-full bg-slate-50 dark:bg-gray-950">
      <div className="mx-auto w-full max-w-none px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AI Model Configuration</h1>
              <p className="mt-1 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
                Configure the AI models used by chat, extraction, embeddings, and candidate voice transcription.
              </p>
            </div>
            <span className="inline-flex w-fit items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              Global Settings
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-6 h-6 border-2 rounded-full" style={{ borderColor: `${primaryColor} transparent` }} />
          </div>
        ) : (
          <div className="space-y-6">
            <section className="rounded-2xl border border-cyan-200 bg-cyan-50/70 p-5 shadow-sm dark:border-cyan-900/60 dark:bg-cyan-950/20">
              <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">AI Audio</h2>
                    <span className="rounded-full bg-cyan-100 px-2 py-0.5 text-[11px] font-semibold uppercase text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-200">Candidate Interview TTS</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                    Controls the AI interviewer audio used by voice interviews and recordings.
                  </p>
                </div>
                <span className="text-xs px-2 py-1 rounded font-mono bg-white text-cyan-700 dark:bg-gray-900 dark:text-cyan-300">
                  VOICE_TTS
                </span>
              </div>

              <div className="rounded-xl bg-white p-4 dark:bg-gray-900">
                <div className="grid gap-3 lg:grid-cols-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">TTS Engine</label>
                    <select
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900"
                      value={selectedTtsEngine}
                      onChange={e => handleMediaTtsChange('engine', e.target.value)}
                    >
                      {ttsEngines.map(engine => (
                        <option key={engine} value={engine}>{engine === 'elevenlabs' ? 'ElevenLabs' : engine.charAt(0).toUpperCase() + engine.slice(1)}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">ElevenLabs Model</label>
                    <select
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900 disabled:opacity-50"
                      value={mediaTts.elevenlabs_model || elevenLabsModels[0] || ''}
                      onChange={e => handleMediaTtsChange('elevenlabs_model', e.target.value)}
                      disabled={!isElevenLabsTts}
                    >
                      {elevenLabsModels.map(model => <option key={model} value={model}>{model}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Character</label>
                    <select
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900 disabled:opacity-50"
                      value={mediaTts.elevenlabs_voice_id || elevenLabsVoices[0]?.voice_id || ''}
                      onChange={e => handleMediaTtsChange('elevenlabs_voice_id', e.target.value)}
                      disabled={!isElevenLabsTts}
                    >
                      {elevenLabsVoices.map(voice => (
                        <option key={voice.voice_id} value={voice.voice_id}>{voice.name} - {voice.gender}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">ElevenLabs API Key</label>
                    <div className="relative">
                      <input
                        type={showElevenLabsKey ? 'text' : 'password'}
                        className="w-full px-3 py-2.5 pr-16 truncate rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900 disabled:opacity-50"
                        value={mediaTts.elevenlabs_api_key || ''}
                        onChange={e => handleMediaTtsChange('elevenlabs_api_key', e.target.value)}
                        placeholder={mediaTts.has_elevenlabs_api_key ? 'Saved (enter to change)' : 'Enter API key'}
                        disabled={!isElevenLabsTts}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs disabled:opacity-50"
                        onClick={() => setShowElevenLabsKey(prev => !prev)}
                        disabled={!isElevenLabsTts}
                      >
                        {showElevenLabsKey ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {mediaTts.has_elevenlabs_api_key && !mediaTts.elevenlabs_api_key && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">Key saved in database</p>
                    )}
                  </div>
                </div>

                {mediaTestResult && (
                  <div className={`mt-4 flex items-start gap-2 text-sm p-3 rounded-lg ${mediaTestResult.success ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 border border-green-200 dark:border-green-800' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 border border-red-200 dark:border-red-800'}`}>
                    <StatusIcon success={mediaTestResult.success} />
                    <span>{mediaTestResult.message}</span>
                  </div>
                )}

                <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                  <button
                    onClick={testMediaTts}
                    disabled={mediaTesting || (isElevenLabsTts && !(mediaTts.elevenlabs_api_key || mediaTts.has_elevenlabs_api_key))}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 bg-white dark:border-gray-700 text-gray-700 dark:bg-gray-900 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
                  >
                    {mediaTesting ? 'Testing...' : 'Test Connection'}
                  </button>
                  <button
                    onClick={saveMediaTts}
                    disabled={mediaSaving}
                    className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50 transition-colors"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {mediaSaving ? 'Saving...' : 'Save Audio Settings'}
                  </button>
                  <button
                    onClick={resetMediaTts}
                    disabled={mediaSaving}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 bg-white dark:border-gray-700 text-gray-700 dark:bg-gray-900 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors sm:ml-auto"
                  >
                    Reset Defaults
                  </button>
                </div>
              </div>
            </section>

            {VOICE_TIER && (() => {
              const { key, label, desc, defaultTemp, defaultTokens } = VOICE_TIER;
              const mainData = mainState[key] || {};
              const isSaved = saveSuccess === `${key}:main`;

              return (
                <section key={key} className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5 shadow-sm dark:border-emerald-900/60 dark:bg-emerald-950/20">
                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{label}</h2>
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold uppercase text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200">Candidate STT</span>
                      </div>
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{desc}</p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Browser transcript is used as fallback when online transcription is unavailable.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isSaved && (
                        <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">Saved</span>
                      )}
                      <span className="text-xs px-2 py-1 rounded font-mono bg-white text-emerald-700 dark:bg-gray-900 dark:text-emerald-300">
                        {key}
                      </span>
                    </div>
                  </div>
                  <ProviderConfigForm
                    tierKey={key}
                    role="main"
                    data={mainData}
                    onChange={handleFieldChange}
                    onSave={handleSave}
                    onTest={handleTest}
                    saving={saving}
                    testing={testing}
                    testResult={testResult}
                    primaryColor={primaryColor}
                    isEmbedding={false}
                    isVoiceStt
                    showApiKey={showApiKey[`${key}:main`]}
                    toggleApiKey={() => setShowApiKey(prev => ({ ...prev, [`${key}:main`]: !prev[`${key}:main`] }))}
                    defaultTemp={defaultTemp}
                    defaultTokens={defaultTokens}
                  />
                </section>
              );
            })()}

            <div>
              <div className="mb-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Text and embedding models</h2>
              </div>
            </div>

            {TEXT_TIERS.map(({ key, label, desc, defaultTemp, defaultTokens }) => {
              const mainData = mainState[key] || {};
              const fbData = fallbackState[key] || {};
              const isEmbedding = key === 'EMBEDDING';
              const isSaved = saveSuccess === `${key}:main`;
              const isFbSaved = saveSuccess === `${key}:fallback`;
              const fbConfigured = fbData.configured;
              const fbExpanded = expandedFallback[key];

              return (
                <div key={key} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{label} Tier</h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{desc}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isSaved && (
                        <span className="text-xs px-2 py-1 rounded bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400">Saved</span>
                      )}
                      <span className="text-xs px-2 py-1 rounded font-mono" style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}>
                        {key}
                      </span>
                    </div>
                  </div>

                  {/* Main provider */}
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Primary Provider</div>
                    <ProviderConfigForm
                      tierKey={key}
                      role="main"
                      data={mainData}
                      onChange={handleFieldChange}
                      onSave={handleSave}
                      onTest={handleTest}
                      saving={saving}
                      testing={testing}
                      testResult={testResult}
                      primaryColor={primaryColor}
                      isEmbedding={isEmbedding}
                      isVoiceStt={false}
                      showApiKey={showApiKey[`${key}:main`]}
                      toggleApiKey={() => setShowApiKey(prev => ({ ...prev, [`${key}:main`]: !prev[`${key}:main`] }))}
                      defaultTemp={defaultTemp}
                      defaultTokens={defaultTokens}
                    />
                  </div>

                  {/* Fallback section */}
                  <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
                    {!fbExpanded ? (
                      <button
                        onClick={() => setExpandedFallback(prev => ({ ...prev, [key]: true }))}
                        className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Fallback Provider
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">(optional — auto-switches when main is down)</span>
                      </button>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                            Fallback Provider
                            {fbConfigured && <span className="text-green-600 dark:text-green-400 ml-2">configured</span>}
                          </div>
                          {isFbSaved && (
                            <span className="text-xs px-2 py-1 rounded bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400">Saved</span>
                          )}
                        </div>
                        <ProviderConfigForm
                          tierKey={key}
                          role="fallback"
                          data={fbData}
                          onChange={handleFieldChange}
                          onSave={handleSave}
                          onTest={handleTest}
                          onRemove={handleRemoveFallback}
                          saving={saving}
                          testing={testing}
                          testResult={testResult}
                          primaryColor={primaryColor}
                          isEmbedding={isEmbedding}
                          isVoiceStt={false}
                          showApiKey={showApiKey[`${key}:fallback`]}
                          toggleApiKey={() => setShowApiKey(prev => ({ ...prev, [`${key}:fallback`]: !prev[`${key}:fallback`] }))}
                          defaultTemp={defaultTemp}
                          defaultTokens={defaultTokens}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
